import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  CircularProgress,
  Alert,
  TextField,
  MenuItem,
  Stack,
  IconButton,
  Tooltip,
  Collapse,
  Card,
  CardContent,
  Badge,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  CheckCircle as SuccessIcon,
  Error as ErrorIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Computer as ComputerIcon,
  LocationOn as LocationIcon,
  Timer as TimerIcon,
  Security as SecurityIcon,
  Code as CodeIcon,
  Info as InfoIcon,
  Link as LinkIcon,
} from '@mui/icons-material';
import { useGetAuditTrailAsUser, useListAccessibleFirms, UserRole } from '@taxassist/api-client';
import type {
  GetAuditTrailAsUser200RecordsItem,
  GetAuditTrailAsUserAction,
  GetAuditTrailAsUserResourceType,
} from '@taxassist/api-client';
import { format, parseISO } from 'date-fns';
import { useAuth } from '../../contexts/AuthContext';

interface AuditLogFilters {
  action?: GetAuditTrailAsUserAction;
  resourceType?: GetAuditTrailAsUserResourceType;
  success?: boolean;
}

interface ExpandedRowState {
  [auditId: string]: boolean;
}

const actionColors: Record<GetAuditTrailAsUserAction, 'primary' | 'secondary' | 'success' | 'error' | 'warning' | 'info'> = {
  CREATE: 'success',
  READ: 'info',
  UPDATE: 'warning',
  DELETE: 'error',
  SUBMIT: 'primary',
  UPLOAD: 'secondary',
  LOGIN: 'success',
  LOGOUT: 'secondary',
  ACCESS_DENIED: 'error',
};

const resourceTypeColors: Record<GetAuditTrailAsUserResourceType, 'primary' | 'secondary' | 'success' | 'error' | 'warning' | 'info'> = {
  FIRM: 'primary',
  JOB: 'success',
  DOCUMENT: 'info',
  USER: 'warning',
  SYSTEM: 'secondary',
};

export const OpsAuditLogsPage: React.FC = () => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [filters, setFilters] = useState<AuditLogFilters>({});
  const [pageTokens, setPageTokens] = useState<(string | undefined)[]>([undefined]); // Store tokens for each page
  const [selectedFirmId, setSelectedFirmId] = useState<string>('');
  const [expandedRows, setExpandedRows] = useState<ExpandedRowState>({});
  const { user, isAuthenticated } = useAuth();

  // Check if user has access
  const hasAccess = user?.role === UserRole.admin;

  // Fetch firms list for selection
  const { data: firmsData, isLoading: firmsLoading, error: firmsError } = useListAccessibleFirms({}, {
    query: {
      enabled: isAuthenticated && hasAccess,
      queryKey: ['accessible-firms'],
    }
  });

  // Handle both old (array) and new (object with firms array) response formats
  const firms = (
    Array.isArray(firmsData) 
      ? firmsData  // Old format: direct array
      : firmsData?.firms || []  // New format: object with firms property
  ).sort((a, b) => a.name.localeCompare(b.name));


  // Get the nextToken for the current page
  const currentPageToken = pageTokens[page];

  // Fetch audit trail data with proper pagination
  const { data: auditData, isLoading, error, refetch } = useGetAuditTrailAsUser({
    limit: rowsPerPage,
    action: filters.action,
    resourceType: filters.resourceType,
    nextToken: currentPageToken,
    firmId: selectedFirmId, // Now required - firm must be selected
  }, {
    query: {
      enabled: isAuthenticated && hasAccess && !!selectedFirmId, // Only fetch when firm is selected
      staleTime: 5 * 60 * 1000, // 5 minutes
      // Include selectedFirmId in query key to ensure proper caching per firm
      queryKey: ['auditTrail', selectedFirmId, page, rowsPerPage, filters.action, filters.resourceType, currentPageToken],
    }
  });


  // Update page tokens when new data is received
  React.useEffect(() => {
    if (auditData?.nextToken && !pageTokens[page + 1]) {
      setPageTokens(prev => {
        const newTokens = [...prev];
        newTokens[page + 1] = auditData.nextToken;
        return newTokens;
      });
    }
  }, [auditData?.nextToken, page, pageTokens]);

  const handleChangePage = (_: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
    setPageTokens([undefined]); // Reset pagination tokens
  };

  const handleFilterChange = (field: keyof AuditLogFilters, value: string | boolean | undefined) => {
    setFilters(prev => ({
      ...prev,
      [field]: value || undefined,
    }));
    setPage(0);
    setPageTokens([undefined]); // Reset pagination tokens when filters change
  };

  const handleFirmChange = (firmId: string) => {
    setSelectedFirmId(firmId);
    setPage(0); // Reset to first page when firm changes
    setPageTokens([undefined]); // Reset pagination tokens
    setFilters({}); // Clear filters when firm changes for fresh start
  };

  const formatTimestamp = (timestamp: string) => {
    try {
      return format(parseISO(timestamp), 'MMM dd, yyyy HH:mm:ss');
    } catch {
      return timestamp;
    }
  };

  const getSuccessIcon = (success: boolean) => {
    return success ? (
      <SuccessIcon color="success" fontSize="small" />
    ) : (
      <ErrorIcon color="error" fontSize="small" />
    );
  };

  const toggleRowExpansion = (auditId: string) => {
    setExpandedRows(prev => ({
      ...prev,
      [auditId]: !prev[auditId]
    }));
  };

  const formatJsonValue = (value: any): string => {
    if (value === null || value === undefined) return '-';
    if (typeof value === 'string') return value;
    if (typeof value === 'boolean') return value.toString();
    if (typeof value === 'number') return value.toString();
    return JSON.stringify(value, null, 2);
  };

  const renderMetadataCard = (title: string, icon: React.ReactNode, data: Record<string, any>, emptyMessage: string = 'No data available'): React.ReactElement => {
    const hasData = data && Object.keys(data).length > 0;
    
    return (
      <Card variant="outlined" sx={{ mb: 2 }}>
        <CardContent sx={{ pb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            {icon}
            <Typography variant="h6" sx={{ ml: 1 }}>
              {title}
            </Typography>
          </Box>
          {hasData ? (
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 2 }}>
              {Object.entries(data).map(([key, value]) => (
                <Box key={key}>
                  <Typography variant="caption" color="text.secondary" display="block">
                    {key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}
                  </Typography>
                  <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
                    {formatJsonValue(value)}
                  </Typography>
                </Box>
              ))}
            </Box>
          ) : (
            <Typography variant="body2" color="text.secondary">
              {emptyMessage}
            </Typography>
          )}
        </CardContent>
      </Card>
    );
  };

  // Handle different response formats - the API returns 'records'
  const records = auditData?.records || [];


  // Show access denied for unauthorized users
  if (!hasAccess) {
    return (
      <Box>
        <Typography variant="h4" gutterBottom>
          Audit Logs
        </Typography>
        <Alert severity="error">
          Access denied. Only Admin users can access audit logs.
        </Alert>
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Typography variant="h4" gutterBottom>
          Audit Logs
        </Typography>
        <Tooltip title="Refresh">
          <IconButton onClick={() => refetch()} disabled={isLoading}>
            <RefreshIcon />
          </IconButton>
        </Tooltip>
      </Box>

      <Typography variant="body1" color="text.secondary" paragraph>
        Recent audit trail showing all system activities in reverse chronological order.
      </Typography>

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Filters
        </Typography>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
          <TextField
            select
            label="Firm"
            value={selectedFirmId}
            onChange={(e) => handleFirmChange(e.target.value)}
            sx={{ minWidth: 200 }}
            size="small"
            required
            helperText={
              !selectedFirmId ? "Please select a firm to view audit logs" : 
              firmsLoading ? "Loading firms..." :
              firmsError ? `Error loading firms: ${firmsError.message}` :
              firms.length === 0 ? "No firms available" : ""
            }
          >
            <MenuItem value="">
              <em>Select a firm</em>
            </MenuItem>
            {firms.map((firm) => (
              <MenuItem key={firm.firmId} value={firm.firmId}>
                {firm.name}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            label="Action"
            value={filters.action || ''}
            onChange={(e) => handleFilterChange('action', e.target.value as GetAuditTrailAsUserAction)}
            disabled={!selectedFirmId}
            sx={{ minWidth: 150 }}
            size="small"
          >
            <MenuItem value="">All Actions</MenuItem>
            <MenuItem value="CREATE">Create</MenuItem>
            <MenuItem value="READ">Read</MenuItem>
            <MenuItem value="UPDATE">Update</MenuItem>
            <MenuItem value="DELETE">Delete</MenuItem>
            <MenuItem value="SUBMIT">Submit</MenuItem>
            <MenuItem value="UPLOAD">Upload</MenuItem>
            <MenuItem value="LOGIN">Login</MenuItem>
            <MenuItem value="LOGOUT">Logout</MenuItem>
            <MenuItem value="ACCESS_DENIED">Access Denied</MenuItem>
          </TextField>

          <TextField
            select
            label="Resource Type"
            value={filters.resourceType || ''}
            onChange={(e) => handleFilterChange('resourceType', e.target.value as GetAuditTrailAsUserResourceType)}
            disabled={!selectedFirmId}
            sx={{ minWidth: 150 }}
            size="small"
          >
            <MenuItem value="">All Resources</MenuItem>
            <MenuItem value="FIRM">Firm</MenuItem>
            <MenuItem value="JOB">Job</MenuItem>
            <MenuItem value="DOCUMENT">Document</MenuItem>
            <MenuItem value="USER">User</MenuItem>
            <MenuItem value="SYSTEM">System</MenuItem>
          </TextField>
        </Stack>
      </Paper>

      {/* Show firms loading status */}
      {firmsLoading && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Loading firms list...
        </Alert>
      )}

      {firmsError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          <Typography variant="subtitle2">Error loading firms:</Typography>
          <Typography variant="body2">{firmsError.message}</Typography>
        </Alert>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          <Typography variant="subtitle2">Error loading audit logs:</Typography>
          <Typography variant="body2">{error.message}</Typography>
          {process.env.NODE_ENV === 'development' && (
            <Typography variant="caption" sx={{ mt: 1, display: 'block' }}>
              Debug info: {JSON.stringify(error, null, 2)}
            </Typography>
          )}
        </Alert>
      )}

      <Paper>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell width="40px"></TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Timestamp</TableCell>
                <TableCell>User & Action</TableCell>
                <TableCell>Resource</TableCell>
                <TableCell>Context</TableCell>
                <TableCell>Technical</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                    <CircularProgress size={40} />
                  </TableCell>
                </TableRow>
              ) : records.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                    <Typography color="text.secondary">
                      No audit logs found
                    </Typography>
                    {auditData && (
                      <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                        API response received but no records returned.
                        {auditData.totalCount !== undefined && ` Total count: ${auditData.totalCount}`}
                        {auditData.nextToken && ` (Has next page)`}
                      </Typography>
                    )}
                  </TableCell>
                </TableRow>
              ) : (
                records.map((record: GetAuditTrailAsUser200RecordsItem) => {
                  const isExpanded = expandedRows[record.auditId] || false;
                  const hasMetadata: boolean = !!(record.metadata && typeof record.metadata === 'object' && Object.keys(record.metadata).length > 0);
                  const hasChanges: boolean = !!(record.changes && typeof record.changes === 'object' && Object.keys(record.changes).length > 0);
                  const hasTechnicalDetails: boolean = !!(record.requestId || record.sourceIp || record.userAgent);
                  const hasAdditionalInfo: boolean = hasMetadata || hasChanges || hasTechnicalDetails || !!(record.metadata?.sessionId) || !!(record.metadata?.correlationId);
                  
                  return (
                    <React.Fragment key={record.auditId}>
                      <TableRow hover sx={{ cursor: hasAdditionalInfo ? 'pointer' : 'default' }} onClick={() => hasAdditionalInfo && toggleRowExpansion(record.auditId)}>
                        <TableCell>
                          {hasAdditionalInfo ? (
                            <IconButton size="small">
                              {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                            </IconButton>
                          ) : null}
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            {getSuccessIcon(record.success)}
                            {record.errorCode && (
                              <Tooltip title={record.errorMessage || 'Error occurred'}>
                                <Typography variant="caption" color="error.main">
                                  {record.errorCode}
                                </Typography>
                              </Tooltip>
                            )}
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" fontWeight="medium">
                            {formatTimestamp(record.timestamp)}
                          </Typography>
                          {record.eventType && (
                            <Chip
                              label={record.eventType}
                              size="small"
                              variant="outlined"
                              color="info"
                              sx={{ mt: 0.5 }}
                            />
                          )}
                        </TableCell>
                        <TableCell>
                          <Box>
                            <Typography variant="body2" fontWeight="medium">
                              {record.userEmail}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {record.userRole} • {record.authType}
                            </Typography>
                            <Box sx={{ mt: 0.5 }}>
                              <Chip
                                label={record.action}
                                color={actionColors[record.action] || 'default'}
                                size="small"
                              />
                            </Box>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box>
                            <Chip
                              label={record.resourceType}
                              color={resourceTypeColors[record.resourceType] || 'default'}
                              size="small"
                              variant="outlined"
                            />
                            <Typography variant="body2" sx={{ mt: 0.5, maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                              {record.resourceId}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box>
                            <Typography variant="body2">
                              <strong>Firm:</strong> {String(record.firmId || 'N/A')}
                            </Typography>
                            {record.metadata?.sessionId ? (
                              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                Session: {String(record.metadata.sessionId).slice(0, 8)}...
                              </Typography>
                            ) : null}
                            {record.metadata?.correlationId ? (
                              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                Correlation: {String(record.metadata.correlationId).slice(0, 8)}...
                              </Typography>
                            ) : null}
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                            {record.requestId && (
                              <Tooltip title={`Request ID: ${record.requestId}`}>
                                <Typography variant="caption" color="primary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                  <CodeIcon fontSize="small" />
                                  {record.requestId.slice(0, 8)}...
                                </Typography>
                              </Tooltip>
                            )}
                            {record.sourceIp && (
                              <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <LocationIcon fontSize="small" />
                                {record.sourceIp}
                              </Typography>
                            )}
                            {hasAdditionalInfo && (
                              <Badge color="info" variant="dot" sx={{ mt: 0.5 }}>
                                <InfoIcon fontSize="small" color="action" />
                              </Badge>
                            )}
                          </Box>
                        </TableCell>
                      </TableRow>
                      
                      {hasAdditionalInfo && (
                        <TableRow>
                          <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={7}>
                            <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                              <Box sx={{ margin: 2 }}>
                                <Typography variant="h6" gutterBottom component="div">
                                  Detailed Information
                                </Typography>
                                
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                  {/* Technical Details */}
                                  {hasTechnicalDetails ? (
                                    <Box>
                                      {renderMetadataCard(
                                        'Technical Details',
                                        <ComputerIcon color="primary" />,
                                        {
                                          ...(record.requestId ? { 'Request ID': String(record.requestId) } : {}),
                                          ...(record.sourceIp ? { 'Source IP': String(record.sourceIp) } : {}),
                                          ...(record.userAgent ? { 'User Agent': String(record.userAgent) } : {}),
                                        },
                                        'No technical details available'
                                      ) as React.ReactElement}
                                    </Box>
                                  ) : null}
                                  
                                  {/* Session & Correlation */}
                                  {(record.metadata?.sessionId || record.metadata?.correlationId) ? (
                                    <Box>
                                      {renderMetadataCard(
                                        'Session & Correlation',
                                        <LinkIcon color="secondary" />,
                                        {
                                          ...(record.metadata?.sessionId ? { 'Session ID': record.metadata.sessionId } : {}),
                                          ...(record.metadata?.correlationId ? { 'Correlation ID': record.metadata.correlationId } : {}),
                                        },
                                        'No session information available'
                                      ) as React.ReactElement}
                                    </Box>
                                  ) : null}
                                  
                                  {/* Changes */}
                                  {hasChanges ? (
                                    <Box>
                                      {renderMetadataCard(
                                        'Changes',
                                        <TimerIcon color="warning" />,
                                        (record.changes && typeof record.changes === 'object') ? record.changes : {},
                                        'No changes recorded'
                                      ) as React.ReactElement}
                                    </Box>
                                  ) : null}
                                  
                                  {/* Metadata */}
                                  {hasMetadata ? (
                                    <Box>
                                      {renderMetadataCard(
                                        'Metadata',
                                        <InfoIcon color="info" />,
                                        (record.metadata && typeof record.metadata === 'object') ? record.metadata : {},
                                        'No metadata available'
                                      ) as React.ReactElement}
                                    </Box>
                                  ) : null}
                                  
                                  {/* Error Details */}
                                  {(!record.success && record.errorMessage) ? (
                                    <Box>
                                      {renderMetadataCard(
                                        'Error Details',
                                        <SecurityIcon color="error" />,
                                        {
                                          'Error Code': record.errorCode || 'N/A',
                                          'Error Message': record.errorMessage,
                                        },
                                        'No error details available'
                                      ) as React.ReactElement}
                                    </Box>
                                  ) : null}
                                </Box>
                              </Box>
                            </Collapse>
                          </TableCell>
                        </TableRow>
                      )}
                    </React.Fragment>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
        
        <TablePagination
          rowsPerPageOptions={[10, 25, 50, 100]}
          component="div"
          count={auditData?.totalCount || -1} // Use -1 for unknown count with token pagination
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          // Disable next page button when no more data available
          nextIconButtonProps={{
            disabled: !auditData?.nextToken
          }}
          // Show proper page info for token-based pagination
          labelDisplayedRows={({ from, to, count }) => {
            if (count === -1) {
              return `${from}–${to} of many`;
            }
            return `${from}–${to} of ${count !== -1 ? count : 'more than ' + to}`;
          }}
        />
      </Paper>
    </Box>
  );
};